<template>
    <b-modal
        :visible="true"
        size="xl"
        title="개선 제안"
        scrollable
        no-close-on-backdrop
        no-close-on-esc
        hide-header-close
    >
        <!-- 1) 아직 개선 제안이 없으면(신규 진입) 생성 카드 — LLM 호출이 비싸므로
             자동 실행하지 않고 명시적으로 누르게 한다. 어디에 반영할지(규칙/사례)는
             AI 가 정한다 — 평가자는 대상을 고르지 않는다. -->
        <b-card v-if="!proposal && !loading" no-body class="p-3 mb-3">
            <b-form-group label="AI 에게 추가로 당부할 점 (선택)" label-for="preview-instruction">
                <b-form-input
                    id="preview-instruction"
                    v-model="instruction"
                    :disabled="planning"
                    placeholder="예: 기존 판단 기준의 문체를 유지하고, 새 기준은 꼭 필요한 것만 추가해 주세요"
                />
            </b-form-group>
            <div class="d-flex align-items-center">
                <b-button variant="primary" :disabled="planning || !feedbackIds.length" @click="generate">
                    <b-spinner v-if="planning" small class="mr-1" />
                    AI 판단 기준에 반영하기
                </b-button>
                <span class="ml-3 text-muted">
                    선택한 피드백 {{ feedbackIds.length }}건을 AI 가 앞으로의 판정에 반영합니다.
                </span>
            </div>
        </b-card>

        <div v-if="loading" class="text-center text-muted py-4">
            <b-spinner small class="mr-2" />
            개선 제안을 불러오는 중입니다.
        </div>
        <b-alert :show="!!loadError" variant="danger" class="py-2">
            {{ loadError }}
        </b-alert>

        <!-- 2) 개선 제안 상세 -->
        <template v-if="proposal">
            <b-card no-body class="p-3 mb-3">
                <div class="d-flex flex-wrap align-items-center">
                    <strong class="mr-3">개선 제안 #{{ proposal.id }}</strong>
                    <b-badge :variant="proposalStatusVariant" class="mr-3">
                        {{ proposalStatusLabel }}
                    </b-badge>
                    <span class="text-muted mr-3">피드백 {{ feedbackCount }}건</span>
                    <span v-if="aiTargetsLabel" class="text-muted mr-3">
                        AI 판단: {{ aiTargetsLabel }}
                    </span>
                    <span v-if="targetTotal" class="text-muted">
                        진행 {{ targetDone }}/{{ targetTotal }}
                    </span>
                </div>
                <div v-if="proposal.rationale" class="mt-2 small proposal-rationale">
                    {{ proposal.rationale }}
                </div>
            </b-card>

            <!-- 피드백별 처리 — 한 피드백이 여러 초안(규칙/사례)으로 갈렸는지 한눈에. -->
            <b-card v-if="dispositionSummary.length" no-body class="p-3 mb-3">
                <strong class="d-block mb-2">피드백별 처리</strong>
                <div
                    v-for="row in dispositionSummary"
                    :key="row.feedbackId"
                    class="mb-2 pb-2 border-bottom disposition-summary-row"
                >
                    <div class="small text-muted">
                        <span v-if="row.subCode">{{ row.subCode }} · </span>{{ row.content }}
                    </div>
                    <div class="mt-1">
                        <b-badge
                            v-for="(e, i) in row.entries"
                            :key="i"
                            :variant="dispositionVariant(e.action)"
                            class="mr-2"
                        >
                            {{ e.targetLabel }}: {{ dispositionLabel(e.action) }}
                        </b-badge>
                    </div>
                </div>
            </b-card>

            <!-- targets 0건 — AI 가 반영할 자산이 없다고 판단. 자동 종료하지 않고 사람 확정이
                 필요하지만, 현재 백엔드에는 확정/대상추가 API 가 없어 안내만 한다. -->
            <b-alert v-if="!targetTotal" show variant="secondary">
                <strong>AI 제안: 반영할 자산 없음</strong>
                <div class="mt-1">{{ proposal.rationale || '-' }}</div>
                <div class="small mt-2">
                    이 자산들에는 바꿀 내용이 없다고 확정하면 이 피드백은 반영 완료로 처리됩니다.
                </div>
                <b-button
                    v-if="proposal.status === 'OPEN'"
                    variant="outline-secondary"
                    size="sm"
                    class="mt-2"
                    :disabled="acting"
                    @click="confirmNoChange"
                >
                    <b-spinner v-if="acting" small class="mr-1" />
                    반영 불필요로 확정
                </b-button>
            </b-alert>

            <!-- 초안(target) 카드 — 각자 job 생성·폴링·저장·스킵을 독립 수행. -->
            <ApplyPreviewDraft
                v-for="target in proposal.targets"
                :key="target.id"
                :target="target"
                :feedback-items="proposalFeedbackItems"
                @patch-target="onPatchTarget"
                @saved="onProposalReplaced"
                @proposal-updated="onProposalReplaced"
                @refresh-requested="onRefreshRequested"
            />
        </template>

        <template #modal-footer>
            <div class="d-flex w-100 align-items-center">
                <span class="mr-auto text-muted small">
                    <template v-if="proposal && targetTotal">
                        {{ targetDone }}/{{ targetTotal }} 초안 처리
                        <span v-if="isCompleted" class="text-success font-weight-bold ml-1">
                            · 개선안 완료
                        </span>
                    </template>
                </span>
                <b-button variant="secondary" @click="attemptClose">닫기</b-button>
            </div>
        </template>
    </b-modal>
</template>

<script>
import { getErrorMessage } from '@core/utils/api/errorMessage';
import {
    ACCEPTED_TARGET_TERMINALS,
    DISPOSITION_LABELS,
    DISPOSITION_VARIANTS,
    previewTargetLabel,
} from './prompt-labels';
import {clearActiveProposal, saveActiveProposal} from './preview-job-store';
import ApplyPreviewDraft from './ApplyPreviewDraft.vue';

const PROPOSAL_STATUS_LABELS = {
    OPEN: '진행 중',
    COMPLETED: '완료',
    NO_CHANGE: '반영 불필요',
    CANCELLED: '취소됨',
};
const PROPOSAL_STATUS_VARIANTS = {
    OPEN: 'primary',
    COMPLETED: 'success',
    NO_CHANGE: 'secondary',
    CANCELLED: 'light',
};

export default {
    name: 'ApplyPreviewModal',
    components: {ApplyPreviewDraft},
    props: {
        // 신규 진입: 반영할 피드백 id 들(인박스 선택).
        feedbackIds: {type: Array, default: () => []},
        // 신규 진입 시 피드백 원문(disposition 표에 sub_code/content 를 붙인다).
        feedbackItems: {type: Array, default: () => []},
        // "개선안 계속 보기": 인박스 행의 proposal 요약을 seed 로 받는다({id,status,rationale,targets}).
        initialProposal: {type: Object, default: null},
        // localStorage 이어보기: proposal id 로 재수화한다.
        proposalId: {type: Number, default: null},
    },
    data() {
        return {
            instruction: '',
            planning: false,
            loading: false,
            loadError: '',
            // zero-target 확정 등 proposal 레벨 액션의 중복클릭 가드.
            acting: false,
            // {id, status, rationale, targets:[TARGET], feedback_ids?}
            proposal: null,
            // 이 proposal 피드백들의 원문(disposition 표시용).
            proposalFeedbackItems: [],
        };
    },
    created() {
        // 진입 책임은 부모에 있다 — 모달이 임의로 store 를 읽어 선택을 덮어쓰지 않는다.
        const seedId = (this.initialProposal && this.initialProposal.id) || this.proposalId;
        if (seedId) {
            this.hydrateProposal(seedId);
        }
        // seed 가 없으면 신규 생성 카드가 뜬다(feedbackIds 로 plan 을 부른다).
    },
    computed: {
        targetTotal() {
            return (this.proposal && this.proposal.targets) ? this.proposal.targets.length : 0;
        },
        targetDone() {
            if (!this.proposal || !this.proposal.targets) return 0;
            return this.proposal.targets.filter(
                (t) => ACCEPTED_TARGET_TERMINALS.includes(t.status)).length;
        },
        isCompleted() {
            return !!this.proposal && this.proposal.status !== 'OPEN';
        },
        // 닫을 때 경고할지 — 저장/스킵하지 않은 READY 초안(직접 수정분이 유실될 수 있음)이 있으면.
        needsCloseConfirm() {
            return !!this.proposal
                && this.proposal.status === 'OPEN'
                && (this.proposal.targets || []).some((t) => t.status === 'READY');
        },
        proposalStatusLabel() {
            return PROPOSAL_STATUS_LABELS[this.proposal.status] || this.proposal.status;
        },
        proposalStatusVariant() {
            return PROPOSAL_STATUS_VARIANTS[this.proposal.status] || 'secondary';
        },
        aiTargetsLabel() {
            if (!this.proposal || !this.proposal.targets) return '';
            return this.proposal.targets
                .map((t) => previewTargetLabel(t.asset_type))
                .join(' · ');
        },
        feedbackCount() {
            if (this.proposalFeedbackItems.length) return this.proposalFeedbackItems.length;
            if (this.proposal && Array.isArray(this.proposal.feedback_ids)) {
                return this.proposal.feedback_ids.length;
            }
            // 폴백: dispositions 의 고유 feedback_id 수.
            const ids = new Set();
            ((this.proposal && this.proposal.targets) || []).forEach((t) => {
                (t.dispositions || []).forEach((d) => ids.add(d.feedback_id));
            });
            return ids.size;
        },
        // 피드백별로 어느 초안에서 어떻게 처리됐는지 묶는다(한 피드백 → 규칙/사례 동시 반영 가시화).
        dispositionSummary() {
            if (!this.proposal || !this.proposal.targets) return [];
            const byId = {};
            this.proposalFeedbackItems.forEach((f) => {
                byId[f.id] = f;
            });
            const rows = {};
            const order = [];
            this.proposal.targets.forEach((t) => {
                const label = previewTargetLabel(t.asset_type);
                (t.dispositions || []).forEach((d) => {
                    if (!rows[d.feedback_id]) {
                        const src = byId[d.feedback_id] || {};
                        rows[d.feedback_id] = {
                            feedbackId: d.feedback_id,
                            subCode: src.sub_code,
                            content: src.content || '(목록에 없는 피드백)',
                            entries: [],
                        };
                        order.push(d.feedback_id);
                    }
                    rows[d.feedback_id].entries.push({targetLabel: label, action: d.action});
                });
            });
            return order.map((id) => rows[id]);
        },
    },
    methods: {
        // 신규 개선 제안 생성 — plan 이 proposal + 0~2 target 을 만들어 준다.
        async generate() {
            this.planning = true;
            try {
                const {data} = await this.$axios.post(
                    '/supplement-prompt/apply-preview/plan', {
                        feedback_ids: this.feedbackIds,
                        instruction: this.instruction || undefined,
                    });
                if (!data.proposal_id || !Array.isArray(data.proposal_targets)) {
                    this.$bvToast.toast(
                        '개선 제안 생성 응답 형식이 올바르지 않습니다. 담당자에게 알려주세요.',
                        {title: '오류', variant: 'danger'});
                    return;
                }
                this.proposal = {
                    id: data.proposal_id,
                    status: data.proposal_status || 'OPEN',
                    rationale: data.rationale || '',
                    feedback_ids: data.feedback_ids || [...this.feedbackIds],
                    targets: data.proposal_targets,
                };
                this.proposalFeedbackItems = this.feedbackItems;
                saveActiveProposal(this.proposal.id);
                this.$emit('proposal-created', {proposalId: this.proposal.id});
            } catch (e) {
                const status = e.response && e.response.status;
                if (status === 409) {
                    // 이미 열린 개선 제안에 포함된 피드백(경쟁 상태). 목록 갱신을 부모에 맡기고 닫는다.
                    this.$bvToast.toast(
                        getErrorMessage(e, '선택한 피드백 중 일부가 이미 진행 중인 개선안에 포함돼 있습니다.'),
                        {title: '진행 중인 개선안', variant: 'warning'});
                    this.$emit('close');
                    return;
                }
                this.$bvToast.toast(
                    getErrorMessage(e, 'AI 가 반영 대상을 결정하지 못했습니다.'),
                    {title: '오류', variant: 'danger'});
            } finally {
                this.planning = false;
            }
        },
        // proposal id 로 최신 proposal + 피드백 원문을 재수화한다.
        // 열린 proposal 의 피드백은 서버 파생 상태가 IN_PROGRESS/READY 뿐이므로 두 상태만
        // 조회한다 — status=ALL 은 최근 APPLIED/DISMISSED 가 500건을 채우면 대상을 놓칠 수 있다.
        // (proposal summary 는 feedback_ids/instruction 을 안 주므로 행에서 모은다.)
        async hydrateProposal(id, {silent = false} = {}) {
            if (!silent) this.loading = true;
            this.loadError = '';
            try {
                const results = await Promise.all([
                    this.fetchFeedbackByStatus('IN_PROGRESS'),
                    this.fetchFeedbackByStatus('READY'),
                ]);
                const seen = new Set();
                const matched = [];
                results.flat().forEach((r) => {
                    if (seen.has(r.id)) return;
                    if (r.proposal && r.proposal.id === id) {
                        seen.add(r.id);
                        matched.push(r);
                    }
                });
                if (!matched.length) {
                    this.loadError = '개선 제안을 찾을 수 없습니다. 이미 완료됐거나 목록이 갱신됐을 수 있습니다.';
                    clearActiveProposal();
                    return;
                }
                this.proposal = {...matched[0].proposal};
                this.proposalFeedbackItems = matched;
                if (this.proposal.status !== 'OPEN') clearActiveProposal();
            } catch (e) {
                this.loadError = getErrorMessage(e, '개선 제안을 불러오지 못했습니다.');
            } finally {
                if (!silent) this.loading = false;
            }
        },
        async fetchFeedbackByStatus(status) {
            const {data} = await this.$axios.get('/supplement-prompt/feedback', {
                params: {status},
            });
            return (data && data.feedback) || [];
        },
        // 자식이 job 접수 실패 등으로 canonical 재수화를 요청하면 조용히 다시 읽는다.
        onRefreshRequested() {
            if (this.proposal && this.proposal.id) {
                this.hydrateProposal(this.proposal.id, {silent: true});
            }
        },
        // 자식이 job 상태를 알려오면 해당 target 만 부분 갱신한다(부모가 소유).
        onPatchTarget({targetId, status, jobId}) {
            if (!this.proposal) return;
            const target = this.proposal.targets.find((t) => t.id === targetId);
            if (!target) return;
            if (status) this.$set(target, 'status', status);
            if (jobId) this.$set(target, 'current_preview_job_id', jobId);
        },
        // targets 0건 proposal 을 사람이 "반영 불필요"로 확정한다(proposal 레벨 API).
        // 응답이 PROPOSAL_ROW 라 target skip/confirm 과 동일하게 onProposalReplaced 로 처리한다.
        async confirmNoChange() {
            const ok = await this.$bvModal.msgBoxConfirm(
                '이 개선 제안은 바꿀 자산이 없다고 확정합니다. 관련 피드백이 반영 완료로 처리됩니다.',
                {title: '반영 불필요 확정', okTitle: '반영 불필요', cancelTitle: '취소'},
            );
            if (!ok) return;
            this.acting = true;
            try {
                const {data} = await this.$axios.post(
                    `/supplement-prompt/proposals/${this.proposal.id}/confirm-no-change`);
                this.onProposalReplaced({proposal: data});
            } catch (e) {
                this.$bvToast.toast(getErrorMessage(e, '반영 불필요로 확정하지 못했습니다.'), {
                    title: '오류', variant: 'danger',
                });
            } finally {
                this.acting = false;
            }
        },
        // 저장/스킵/반영불필요 응답은 PROPOSAL_ROW 전체 — 그대로 교체한다.
        onProposalReplaced({proposal}) {
            if (!proposal || !this.proposal || proposal.id !== this.proposal.id) return;
            this.proposal = {...proposal};
            if (proposal.status !== 'OPEN') {
                clearActiveProposal();
                this.$emit('proposal-completed', {proposalId: proposal.id});
            }
        },
        // 모달 자체 닫기(X/Esc/backdrop)를 모두 끄고 이 footer 버튼 한 경로로만 닫는다
        // — :visible 이 항상 true 라 @hide 가로채기는 부모 v-if 와 충돌(이중 발화)하기 때문.
        async attemptClose() {
            if (this.needsCloseConfirm) {
                const ok = await this.$bvModal.msgBoxConfirm(
                    '저장하지 않은 초안이 있습니다. 닫아도 AI 가 만든 결과는 서버에 남아 이어볼 수 있지만, '
                    + '직접 수정한 내용은 사라집니다.',
                    {title: '미저장 초안', okTitle: '닫기', cancelTitle: '계속 편집'},
                );
                if (!ok) return;
            }
            this.$emit('close');
        },
        dispositionLabel(action) {
            return DISPOSITION_LABELS[action] || action || '-';
        },
        dispositionVariant(action) {
            return DISPOSITION_VARIANTS[action] || 'secondary';
        },
    },
};
</script>

<style scoped>
.proposal-rationale {
    white-space: pre-wrap;
    word-break: break-word;
}

.disposition-summary-row:last-child {
    border-bottom: 0 !important;
    margin-bottom: 0 !important;
    padding-bottom: 0 !important;
}
</style>
